import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  HandHeart, Calendar, MapPin, CheckCircle2, ArrowLeft, Loader2, Users,
} from "lucide-react";

/**
 * VolunteerSignup — public, unauthenticated volunteer sign-up flow at
 * `/volunteer/:eventId`. Organizers share this single URL. Helpers pick
 * a slot (or slots), sign the waiver by typing their name, and confirm.
 *
 * Backend contract: GET/POST /api/events/{id}/volunteers/(public|signup)
 */
export default function VolunteerSignup() {
  const { eventId } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    slot_ids: [],
    waiver_signature: "",
    question: "",
    consent_share_email: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/events/${eventId}/volunteers/public`)
      .then(({ data }) => setConfig(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)
                            || "Volunteer sign-up isn't open for this event yet."))
      .finally(() => setLoading(false));
  }, [eventId]);

  const toggleSlot = (sid) => {
    setForm((f) => ({
      ...f,
      slot_ids: f.slot_ids.includes(sid)
        ? f.slot_ids.filter((s) => s !== sid)
        : [...f.slot_ids, sid],
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.email.trim() || !form.email.includes("@")) {
      return toast.error("Please enter a valid email so the host can reach you.");
    }
    if (config.require_phone && !form.phone.trim()) {
      return toast.error("This event's host requires a phone number.");
    }
    if (config.require_waiver_signature && !form.waiver_signature.trim()) {
      return toast.error("Please sign the waiver by typing your full name.");
    }
    setSubmitting(true);
    try {
      await api.post(`/events/${eventId}/volunteers/signup`, {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        waiver_signature: form.waiver_signature.trim(),
        question: form.question.trim(),
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sign-up failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center" data-testid="volunteer-loading">
        <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-lime)" }} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20" data-testid="volunteer-closed">
        <div className="card-warm rounded-2xl p-10 text-center">
          <HandHeart className="h-10 w-10 mx-auto" style={{ color: "var(--avt-lime)" }} />
          <h1 className="font-serif text-3xl mt-4">Sign-up isn&rsquo;t open</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>{error}</p>
          <Link to="/events" className="btn-primary inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full">
            <ArrowLeft className="h-4 w-4" /> Browse other events
          </Link>
        </div>
      </div>
    );
  }
  if (sent) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16" data-testid="volunteer-sent">
        <div className="card-warm rounded-2xl p-10 text-center">
          <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
               style={{ backgroundColor: "rgba(132,204,22,0.18)" }}>
            <CheckCircle2 className="h-8 w-8" style={{ color: "var(--avt-lime)" }} />
          </div>
          <h1 className="font-serif text-4xl mt-5">You&rsquo;re in.</h1>
          <p className="mt-3 text-base" style={{ color: "var(--ink-soft)" }}>
            Thanks for signing up to help with <strong>{config.event.title}</strong>. The host will
            reach out with details before the event.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/events" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full">
              <ArrowLeft className="h-4 w-4" /> More events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const ev = config.event;
  return (
    <div className="mx-auto max-w-2xl px-6 py-12" data-testid="volunteer-signup-page">
      {/* Hero */}
      <div className="card-warm rounded-2xl overflow-hidden">
        {ev.cover_image && (
          <div className="aspect-[16/6] overflow-hidden">
            <img src={ev.cover_image} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-6">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
               style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A",
                        border: "1.5px solid var(--avt-lime)" }}>
            <HandHeart className="h-3 w-3" /> Volunteer sign-up
          </div>
          <h1 className="font-serif text-4xl mt-3" data-testid="volunteer-event-title">{ev.title}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-sm" style={{ color: "var(--ink-soft)" }}>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {ev.date}</span>
            {(ev.city || ev.location) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {ev.location ? `${ev.location}, ${ev.city}` : ev.city}
              </span>
            )}
          </div>
          {config.instructions && (
            <p className="mt-4 text-sm leading-relaxed"
               style={{ color: "var(--ink)" }} data-testid="volunteer-instructions">
              {config.instructions}
            </p>
          )}
        </div>
      </div>

      {/* Slots */}
      {config.slots?.length > 0 && (
        <section className="mt-6" data-testid="volunteer-slots-section">
          <div className="overline mb-2" style={{ color: "var(--avt-purple)" }}>Pick your shift</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {config.slots.map((s) => {
              const picked = form.slot_ids.includes(s.id);
              const full = s.is_full;
              return (
                <button
                  key={s.id}
                  disabled={full}
                  data-testid={`volunteer-slot-${s.id}`}
                  onClick={() => !full && toggleSlot(s.id)}
                  className="text-left p-4 rounded-xl border-2 transition-all disabled:opacity-50"
                  style={{
                    borderColor: picked ? "var(--avt-lime)" : "var(--border-warm)",
                    backgroundColor: picked ? "rgba(132,204,22,0.10)" : "var(--sand)",
                    cursor: full ? "not-allowed" : "pointer",
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm">{s.label}</span>
                    {picked && <CheckCircle2 className="h-4 w-4" style={{ color: "var(--avt-lime)" }} />}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
                    {s.starts_at} &mdash; {s.ends_at}
                  </div>
                  {s.description && (
                    <div className="text-xs mt-2" style={{ color: "var(--ink-soft)" }}>{s.description}</div>
                  )}
                  <div className="text-[11px] mt-2 inline-flex items-center gap-1"
                       style={{ color: full ? "var(--avt-orange)" : "var(--ink-soft)" }}>
                    <Users className="h-3 w-3" />
                    {full ? "Full" : `${s.remaining} spot${s.remaining === 1 ? "" : "s"} left`}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Contact info */}
      <section className="mt-6 card-warm rounded-2xl p-6 space-y-4" data-testid="volunteer-form">
        <div className="overline" style={{ color: "var(--avt-teal)" }}>Your info</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Name</Label>
            <Input data-testid="volunteer-name" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Jane Doe" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Email</Label>
            <Input data-testid="volunteer-email" type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   placeholder="jane@example.com" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">
            Phone {config.require_phone ? "" : "(optional)"}
          </Label>
          <Input data-testid="volunteer-phone" value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })}
                 placeholder="(555) 555-5555" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Anything to tell the host? (optional)</Label>
          <Textarea data-testid="volunteer-question" rows={3} maxLength={1000}
                    value={form.question}
                    onChange={(e) => setForm({ ...form, question: e.target.value })}
                    placeholder="Skills, questions, dietary needs…" />
        </div>
      </section>

      {/* Waiver */}
      {(config.waiver_body || config.waiver_title) && (
        <section className="mt-6 card-warm rounded-2xl p-6" data-testid="volunteer-waiver">
          <div className="overline" style={{ color: "var(--avt-orange)" }}>
            {config.waiver_title || "Waiver & liability release"}
          </div>
          {config.waiver_body && (
            <pre
              className="mt-3 whitespace-pre-wrap text-sm leading-relaxed p-4 rounded-lg max-h-64 overflow-y-auto"
              style={{ backgroundColor: "var(--sand)", color: "var(--ink)", fontFamily: "inherit" }}
              data-testid="volunteer-waiver-body"
            >
              {config.waiver_body}
            </pre>
          )}
          {config.require_waiver_signature && (
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest">
                Sign by typing your full name
              </Label>
              <Input
                data-testid="volunteer-waiver-signature"
                value={form.waiver_signature}
                onChange={(e) => setForm({ ...form, waiver_signature: e.target.value })}
                placeholder="Type your full name"
                style={{ fontFamily: "cursive", fontSize: "1.1rem" }}
              />
              <p className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>
                By typing your name you agree to the terms above.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Consent + submit */}
      <div className="mt-6 flex items-center gap-2">
        <Checkbox
          id="volunteer-consent"
          data-testid="volunteer-consent"
          checked={form.consent_share_email}
          onCheckedChange={(v) => setForm({ ...form, consent_share_email: !!v })}
        />
        <Label htmlFor="volunteer-consent" className="text-xs cursor-pointer" style={{ color: "var(--ink-soft)" }}>
          The host can email me updates about this event.
        </Label>
      </div>

      <Button
        data-testid="volunteer-submit"
        onClick={submit}
        disabled={submitting}
        className="btn-primary w-full mt-6 rounded-full py-6"
      >
        {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing you up…</> :
          <><HandHeart className="h-4 w-4 mr-2" /> Sign up to help</>}
      </Button>

      <p className="text-[11px] text-center mt-4" style={{ color: "var(--ink-soft)" }}>
        Not affiliated with any political or religious cause &mdash; just people showing up for local events.
      </p>
    </div>
  );
}
